import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Clock, Save, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { index: 0, name: 'Monday' },
  { index: 1, name: 'Tuesday' },
  { index: 2, name: 'Wednesday' },
  { index: 3, name: 'Thursday' },
  { index: 4, name: 'Friday' },
  { index: 5, name: 'Saturday' },
  { index: 6, name: 'Sunday' }
];

export function WeeklyHoursEditor({ sessionToken, onUpdate }) {
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    fetchBusinessHours();
  }, [sessionToken]);

  const fetchBusinessHours = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/availability/hours`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Sort by day_of_week and ensure all 7 days exist
        const sortedHours = DAYS.map(day => {
          const existing = data.business_hours?.find(h => h.day_of_week === day.index);
          return existing || {
            day_of_week: day.index,
            day_name: day.name,
            is_working_day: false,
            start_time: '09:00',
            end_time: '17:00',
            break_start: '12:00',
            break_end: '13:00'
          };
        });
        setHours(sortedHours);
      }
    } catch (error) {
      console.error('Error fetching hours:', error);
      toast.error('Failed to load business hours');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDay = async (dayIndex) => {
    const dayHours = hours.find(h => h.day_of_week === dayIndex);
    const newValue = !dayHours?.is_working_day;
    
    // Optimistic update
    setHours(prev => (prev || []).map(h => 
      h.day_of_week === dayIndex ? { ...h, is_working_day: newValue } : h
    ));

    try {
      setSaving(dayIndex);
      const params = new URLSearchParams({
        day_of_week: dayIndex,
        is_working_day: newValue
      });
      if (newValue && dayHours) {
        params.append('start_time', dayHours.start_time || '09:00');
        params.append('end_time', dayHours.end_time || '17:00');
        params.append('break_start', dayHours.break_start || '12:00');
        params.append('break_end', dayHours.break_end || '13:00');
      }

      const response = await fetch(`${BACKEND_URL}/api/v1/admin/availability/hours?${params}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        toast.success(`${DAYS[dayIndex].name} ${newValue ? 'enabled' : 'disabled'}`);
        onUpdate?.();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      // Revert on error
      setHours(prev => (prev || []).map(h => 
        h.day_of_week === dayIndex ? { ...h, is_working_day: !newValue } : h
      ));
      toast.error('Failed to update day status');
    } finally {
      setSaving(null);
    }
  };

  const handleTimeChange = async (dayIndex, field, value) => {
    const dayHours = hours.find(h => h.day_of_week === dayIndex);
    if (!dayHours) return;

    // Update local state
    setHours(prev => (prev || []).map(h => 
      h.day_of_week === dayIndex ? { ...h, [field]: value } : h
    ));
  };

  const handleSaveDay = async (dayIndex) => {
    const dayHours = hours.find(h => h.day_of_week === dayIndex);
    if (!dayHours) return;

    try {
      setSaving(dayIndex);
      const params = new URLSearchParams({
        day_of_week: dayIndex,
        is_working_day: dayHours.is_working_day,
        start_time: dayHours.start_time,
        end_time: dayHours.end_time,
        break_start: dayHours.break_start,
        break_end: dayHours.break_end
      });

      const response = await fetch(`${BACKEND_URL}/api/v1/admin/availability/hours?${params}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        toast.success(`${DAYS[dayIndex].name} hours updated`);
        onUpdate?.();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save hours');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Card data-testid="weekly-hours-editor">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Loading business hours...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="weekly-hours-editor">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Weekly Business Hours
        </CardTitle>
        <p className="text-sm text-gray-500">Configure when consultations can be booked (Eastern Time)</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {hours.map((dayHours) => {
            const day = DAYS.find(d => d.index === dayHours.day_of_week);
            const isSaving = saving === dayHours.day_of_week;
            
            return (
              <div 
                key={dayHours.day_of_week}
                className={`p-4 rounded-lg border ${
                  dayHours.is_working_day 
                    ? 'bg-white border-gray-200' 
                    : 'bg-gray-50 border-gray-100'
                }`}
                data-testid={`day-config-${day?.name.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={dayHours.is_working_day}
                      onCheckedChange={() => handleToggleDay(dayHours.day_of_week)}
                      disabled={isSaving}
                      data-testid={`toggle-${day?.name.toLowerCase()}`}
                    />
                    <span className={`font-medium ${
                      dayHours.is_working_day ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {day?.name}
                    </span>
                    {!dayHours.is_working_day && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                        Closed
                      </span>
                    )}
                  </div>
                  {dayHours.is_working_day && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSaveDay(dayHours.day_of_week)}
                      disabled={isSaving}
                      data-testid={`save-${day?.name.toLowerCase()}`}
                    >
                      {isSaving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
                
                {dayHours.is_working_day && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500">Start Time</Label>
                      <Input
                        type="time"
                        value={dayHours.start_time || '09:00'}
                        onChange={(e) => handleTimeChange(dayHours.day_of_week, 'start_time', e.target.value)}
                        className="mt-1"
                        data-testid={`start-time-${day?.name.toLowerCase()}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">End Time</Label>
                      <Input
                        type="time"
                        value={dayHours.end_time || '17:00'}
                        onChange={(e) => handleTimeChange(dayHours.day_of_week, 'end_time', e.target.value)}
                        className="mt-1"
                        data-testid={`end-time-${day?.name.toLowerCase()}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Lunch Start</Label>
                      <Input
                        type="time"
                        value={dayHours.break_start || '12:00'}
                        onChange={(e) => handleTimeChange(dayHours.day_of_week, 'break_start', e.target.value)}
                        className="mt-1"
                        data-testid={`lunch-start-${day?.name.toLowerCase()}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Lunch End</Label>
                      <Input
                        type="time"
                        value={dayHours.break_end || '13:00'}
                        onChange={(e) => handleTimeChange(dayHours.day_of_week, 'break_end', e.target.value)}
                        className="mt-1"
                        data-testid={`lunch-end-${day?.name.toLowerCase()}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> All times are in Eastern Time (ET). 
            Slot duration is 30 minutes with a 3-hour minimum booking notice.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default WeeklyHoursEditor;
